<script setup lang="ts">
import CustomButton from "@/components/Button/Button.vue";
import {useRoute, useRouter} from 'vue-router';
import {computed, onBeforeUnmount, onMounted, ref, watch} from "vue";
import {getBoat} from "@/api";
import {Yacht} from "@/models/Yacht";
import {useI18n} from 'vue-i18n';
import {useFormatStore} from "@/stores/formatStore";
import {throttle} from "lodash";
import {ElNotification} from "element-plus";
import LocationPin from "@/components/Map/LocationPin.vue";
import { useComparisonStore } from "@/stores/comparisonStore";
import {useWindowSize} from "@vueuse/core";
import SpecialRequest from "@/components/SpecialRequest/SpecialRequest.vue";
import { getAsset } from "@/helpers/assetsUtils";



const {t} = useI18n();
const router = useRouter();
const route = useRoute();
const formatStore = useFormatStore();
const comparisonStore = useComparisonStore();
const isScrolled = ref(false);
const showEmptyState = ref(false);
const handleScroll = () => {
  isScrolled.value = window.scrollY > 127;
};
const throttledHandleScroll = throttle(handleScroll, 250);
const yachts = ref<Yacht[]>([]);
const status = ref<'loading' | 'success' | 'error'>('loading');


onMounted(async () => {
  comparisonStore.loadFromLocalStorage();

  const ids = route.query.ids as string;
  if (!ids) {
    showEmptyState.value = true;
    return;
  }

  const yachtIds = ids.split(",");

  if (comparisonStore.yachtsToCompare.length === 0) {
    try {
      const promises = yachtIds.map((yachtId) => getBoat(yachtId));
      const results = await Promise.all(promises);

      yachts.value = results.map((result) => result.data as Yacht);

      comparisonStore.yachtsToCompare = yachts.value.map((yacht) => ({
        yacht,
        isDeal: yacht.isDeal || false,
        price: yacht.price,
        finalPrice: yacht.finalPrice || yacht.price,
      }));

      comparisonStore.saveToLocalStorage();

      status.value = "success";
      showEmptyState.value = false;
    } catch (error) {
      console.error("Failed to fetch yachts:", error);
      status.value = "error";
      showEmptyState.value = true;
    }
  } else {
    yachts.value = comparisonStore.yachtsToCompare.map((compYacht) => compYacht.yacht);
    status.value = "success";
    showEmptyState.value = false;
  }

  window.addEventListener("scroll", throttledHandleScroll);
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', throttledHandleScroll);
});


function removeYacht(uuid: number) {

  const updatedIds = comparisonStore.removeYacht(uuid);

  const updatedQuery = { ...route.query, ids: updatedIds.join(",") || undefined };
  router.replace({ query: updatedQuery });

}

const deleteComparisonList = () => {
  comparisonStore.clearComparison();
  showEmptyState.value = true;
};


watch(
    () => comparisonStore.yachtsToCompare,
    (newYachts) => {
      yachts.value = newYachts.map((compYacht) => compYacht.yacht);
    },
    { deep: true }
);


function viewDetails(yacht) {
  const selectedYacht = comparisonStore.findYachtById(yacht.id);

  if (!selectedYacht) {
    console.error(`Yacht with ID ${yacht.id} not found in yachtsToCompare.`);
    return;
  }

  // Navigate to the yacht details page
  router.push({
    path: `/yachts/${yacht.slug}`,
    query: {
      ...route.query,
    },
  });
}

const { width } = useWindowSize();
const isMobile = computed(() => {
  const value = width.value <= 885;
  return value;
});

const yachtAlignment = computed(() => {
  return {
    'justify-between': yachts.value.length > 3 && !isMobile.value,
    'justify-center': yachts.value.length <= 3 && !isMobile.value,
  };
});

function yachtImageRet(yacht) {
  if (yacht.mainImage != null) {
    return yacht.mainImage.name;
  }
  return require('@/assets/images/boat.png');
}

const getStatsForYacht = (yacht) => comparisonStore.getStats(yacht, formatStore);


function closeMapPopup() {
  const mapOverlay = document.getElementById("map-overlay");
  if (mapOverlay) mapOverlay.classList.add("hidden");
}


const activeRow = ref(null);

// Handle row click 
function handleRowClick(index) {
  if (window.matchMedia('(max-width: 768px)').matches) {
    activeRow.value = activeRow.value === index ? null : index;
  }
}

// Handle hover
function handleHover(index, action) {
  if (window.matchMedia('(min-width: 768px)').matches) {
    activeRow.value = action === 'enter' ? index : null;
  }
}

const getLocation = (yachtId) => {
  return computed(() => comparisonStore.getLocationByYachtId(yachtId));
};


</script>

<template>
  <div class="flex flex-col container pt-32" >
    <!-- Navigation and Delete Button -->
    <div class="navdel-button flex justify-between items-center gap-4 sm:gap-0">
      <div class="flex flex-col gap-2 text-start">

        <CustomButton
            class="hover:bg-black hover:text-white hover:border-black"
            :outlined="true"
            :rounded="true"
            :icon="getAsset('icons', 'Arrow_left_16x16.svg')"
            :hoverIcon="getAsset('icons', 'Arrow_left_white_16x16.svg')"
            @click="router.go(-1)"
        >
          {{ t('backToResults') }}
        </CustomButton>
      </div>
      <div class="dsk-button">
        <CustomButton
            class="hover:bg-black hover:text-white hover:border-black"
            :outlined="true"
            :rounded="true"
            @click="deleteComparisonList"
        >
          {{ t('boatComparison.deleteComparisonList') }}
        </CustomButton>
      </div>
    </div>

    <!-- Title Section -->
    <div class="title-section flex justify-between align-center pt-10  pb-10">
      <div class="flex flex-col gap-2 header align-start">
        <h1 class="title responsive-title-3 font-semibold">{{ t('boatComparison.navigateBack') }}</h1>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="showEmptyState" class="flex flex-col justify-center items-center h-80 gap-4">
      <img src="@/assets/images/search-results.svg" alt="Empty State" class="w-40">
      <div class="font-bold responsive-title-5 text-center">
        Did not find what you were looking for?
      </div>
      <div class="responsive-body-medium text-center">
        {{ t('searchResults.sendInquiry') }}
      </div>
      <CustomButton
          @click="$router.push({ path: '/search', query: route.query })"
          :rounded="true"
          :outlined="true"
          class="w-fit text-semibold hover:text-white hover:bg-black hover:border-black"
      >
        {{ t('searchResults.advancedSearch') }}
      </CustomButton>
      <SpecialRequest :t="t" :showSpecialRequestText="false" />

    </div>

    <!-- Comparison List -->
    <div v-else class="comparison-wrapper">
      <div
          class="comparison flex gap-x-4 sm:gap-x-6 lg:gap-x-6 mb-2"
          :class="yachtAlignment"
      >
        <div class="yacht-data-container relative"
             v-for="yacht in yachts"
             :key="yacht.id">

          <div
              class="yacht-data-container-top transition-all sticky h-[430px] w-67 sm:max-w-91 top-17 z-10 flex flex-col bg-gradient-to-r from-white to-gradients-aliceBlue p-4 rounded-12xl"
              :class="{ 'scrolled': isScrolled  }"
          >
            <!-- Yacht Header and Image -->
            <div class="flex flex-col gap-2">

              <div class="flex justify-between items-center">
                <div class="responsive-caption uppercase ">
                  <LocationPin v-model="getLocation(yacht.id).value"  class="location flex gap-1" />
                </div>
              </div>
              <div class="flex h-16 items-center">
                <div class="responsive-title-6 font-bold line-clamp-1"
                     :class="{ 'scrolled': isScrolled && !isMobile }">
                  {{ yacht.model.name || '' }} · {{ yacht.year }} | {{ yacht.name }}
                </div>
              </div>

            </div>
            <div class="flex flex-col gap-2.5 min-h-40">
              <img :src="yachtImageRet(yacht)" alt="boat_placeholder"
                   class="rounded-8xl h-44 ship-bg-mask transition-all object-cover w-96"
                   :class="{ 'max-h-32': isScrolled && !isMobile }"
              >

              <div class="comparison-icons flex gap-2 relative"
                   :class="{ 'scrolled': isScrolled }">
                <div class="border yacht__btn px-4 py-1.5 rounded-12xl cursor-pointer"
                     style="height:fit-content" @click="removeYacht(yacht.id)">
                  <img src="@/assets/icons/svg/Delete_16x16.svg" alt="add to trip"/>
                </div>
              </div>
              <div class="flex flex-col place-content-between py-2.5 gap-y-1">
                <!-- Discount Badge & Special Offer -->
                <div class="flex">
                  <div class="w-full justify-between" v-if="comparisonStore.getYachtData(yacht.id).isDeal">
                    <div class="flex align-center gap-2 pt-[1px] pb-[0.5px]">
                      <div class="responsive-body-small bg-orange-600 text-white w-fit h-fit py-px px-2 rounded-15xl">
                        -{{ comparisonStore.calculateDiscount(comparisonStore.getYachtData(yacht.id).discount) }}%
                      </div>
                      <span class="responsive-body-xxsmall text-orange-600">{{ t('dealOfTheDayUppercase') }}</span>
                    </div>
                  </div>
                  <div class="flex align-center gap-2 pt-[1px] pb-[0.5px]" v-else>
                    <div v-if="comparisonStore.getYachtData(yacht.id).price != comparisonStore.getYachtData(yacht.id).finalPrice"
                         class="responsive-body-small bg-primary-blue text-white w-fit h-fit py-px px-2 rounded-15xl">
                      -{{ comparisonStore.calculateDiscount(comparisonStore.getYachtData(yacht.id).discount) }}%
                    </div>
                    <span v-if="comparisonStore.getYachtData(yacht.id).price != comparisonStore.getYachtData(yacht.id).finalPrice"
                          class="responsive-body-xsmall text-primary-blue">
                    {{ t('specialOfferUppercase') }}
                  </span>
                  </div>
                </div>

                <!-- Pricing Section -->
                <div class="flex w-full">
                  <div class="w-full" :class="{'flex items-end' : comparisonStore.getYachtData(yacht.id).price == comparisonStore.getYachtData(yacht.id).finalPrice}">
                    <div class="flex justify-between w-full items-center mt-0.5">
                      <div>
                        <div class="flex align-baseline gap-2">
            <span class="responsive-title-4 font-semibold">
              {{ formatStore.formatCurrency(comparisonStore.getYachtData(yacht.id).finalPrice) }}
            </span>
                        </div>
                        <div v-if="comparisonStore.getYachtData(yacht.id).price != comparisonStore.getYachtData(yacht.id).finalPrice"
                             class="flex flex-col gap-1 pt-[1.5px] pb-[0.5px]">
            <span class="responsive-body-xsmall text-grey line-through">
              {{ formatStore.formatCurrency(comparisonStore.getYachtData(yacht.id).price) }}
            </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <template v-for="(stat, index) in getStatsForYacht(yacht)" :key="stat.key">
            <div>
              <div
                  :class="[
          'customRow', 
          activeRow === index ? 'bg-primary-green' : '', 
          'sm:cursor-pointer'
        ]"
                  @click.self="handleRowClick(index)"
                  @mouseenter.self="handleHover(index, 'enter')"
                  @mouseleave.self="handleHover(index, 'leave')"
              >
                <span>{{ stat.value }}</span> {{ t(stat.label) }}
              </div>

              <hr v-if="index !== getStatsForYacht(yacht).length - 1" class="custom-hr">
            </div>
          </template>
          <CustomButton
              type="black"
              :rounded="true"
              :outlined="false"
              class="mt-2 w-full "
              @click="viewDetails(yacht)">
            {{ t('boatComparison.viewDetails') }}
          </CustomButton>

        </div>
      </div>
    </div>


    <!--    Delete button in mobile-->
    <div class="block sm:hidden pt-10 navdel-button flex justify-between items-center gap-4 sm:gap-0">
      <div>
        <CustomButton
            class="hover:bg-black hover:text-white hover:border-black"
            :outlined="true"
            :rounded="true"
            @click="deleteComparisonList"
        >
          {{ t('boatComparison.deleteComparisonList') }}
        </CustomButton>
      </div>
    </div>
  </div>

  <!-- Map Popup Overlay -->
  <div id="map-overlay" class="map-popup-overlay hidden" @click="closeMapPopup">
    <div id="map-overlay-map" class="map-popup-map" @click.stop>
      <div @click="closeMapPopup"
           class="cursor-pointer flex items-center justify-center z-50 absolute w-6 h-6 top-8 right-8 border border-black rounded-full p-1.5">
        <img src="@/assets/icons/svg/Close_16x16.svg" alt="">
      </div>
    </div>
  </div>
</template>


<style scoped>
.custom-hr {
  border: none;
  height: 1px;
  background-color: #F8F8F8;

}
.map-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.map-popup-map {
  width: 80%;
  height: 80%;
  background-color: white;
  border-radius: 10px;
  position: relative;
}

.hidden {
  display: none;
}

.border-black {
  border-color: black !important;
}

.customRow {
  display: flex;
  align-items: center;
  padding: 16px;
  border-radius: 10px;
  gap: 5px;
}


.customRow > span {
  font-weight: 600;
  font-size: 16px;
  color: #000000;
}

.yacht__btn:hover {
  border-color: #000 !important;
}

.yacht-data-container {
  min-width: 20vw;
}

@media only screen and (min-width: 765px) {

  .yacht-data-container-top.scrolled {
    row-gap: unset;
    height: 330px;
  }

  .comparison-icons.scrolled {
    display: none;
  }

}

@media only screen and (max-width: 768px) {

  .dsk-button{
    display: none;
  }
  .comparison-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .yacht-data-container {
    flex-shrink: 0;
  }

  .yacht-data-container-top {
    top: 0rem;
    z-index: 10;
  }
}


@media only screen and (max-width: 424px) {
  .navdel-button {
    flex-direction: column;
  }

  .navdel-button > div {
    width: 100%;
  }
  .navdel-button button {
    width: 100% !important;
  }
}

</style>